import React, { useEffect, useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  CircularProgress,
  Paper,
  Alert,
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
} from '@mui/icons-material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTheme } from '../contexts/ThemeContext';
import { useSubscription } from '../contexts/SubscriptionContext';
import { useAuth } from '../contexts/AuthContext';
import { recordSubscriptionTransaction, getSubscription } from '../services/subscriptionService';
import { Capacitor } from '@capacitor/core';

// Declaração do Meta Pixel para TypeScript
declare global {
  interface Window {
    fbq?: (
      action: 'track' | 'init' | 'trackCustom',
      eventName: string,
      params?: Record<string, unknown>
    ) => void;
  }
}

export const CheckoutSuccessPage: React.FC = () => {
  const { theme } = useTheme();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const { refreshSubscription } = useSubscription();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const sessionId = searchParams.get('session_id');

  const processedSessionRef = React.useRef<string | null>(null);

  useEffect(() => {
    const processCheckout = async () => {
      if (!sessionId) {
        setError('Sessão de checkout não encontrada');
        setLoading(false);
        return;
      }

      // Prevent processing the same session multiple times in this component lifecycle
      if (processedSessionRef.current === sessionId) {
        setLoading(false);
        return;
      }

      if (!user) {
        setLoading(false);
        return;
      }

      try {
        if (processedSessionRef.current !== sessionId) {
          processedSessionRef.current = sessionId; // Mark as processing/processed

          // Register transaction
          await refreshSubscription();

          // Add delay to ensure Firestore sync
          await new Promise(resolve => setTimeout(resolve, 1500));

          const updatedSub = await getSubscription(user.uid);

          if (updatedSub && (updatedSub.status === 'active' || updatedSub.status === 'trialing')) {
            const rawPlan = updatedSub.plan;
            // Map plan names for stats
            const planForStats = rawPlan === 'premium' ? 'basic' : rawPlan;
            const period = updatedSub.billingInterval || 'annual';

            // Calculate value for stats
            let value = 0;
            if (planForStats === 'basic') {
              value = period === 'monthly' ? 19.90 : 199.90;
            } else if (planForStats === 'pro') {
              value = period === 'monthly' ? 25.90 : 259.90;
            }

            // Record transaction
            try {
              await recordSubscriptionTransaction({
                userId: user.uid,
                email: user.email || 'N/A',
                userName: user.displayName || 'N/A',
                plan: planForStats,
                period: period,
                value: value,
                provider: 'stripe',
                platform: Capacitor.getPlatform() === 'web' ? 'web' : 'android',
                deviceModel: window.navigator.userAgent,
                osVersion: Capacitor.getPlatform(),
                status: 'active',
                sessionId: sessionId
              });

              // Meta Pixel
              if (typeof window.fbq !== 'undefined') {
                window.fbq('track', 'Purchase', {
                  value: value,
                  currency: 'BRL',
                  content_type: 'subscription',
                  content_name: `${planForStats}_${period}`,
                });
                console.log('[Meta Pixel] Purchase event tracked');
              }
            } catch (e) {
              console.error('Transaction recording error:', e);
            }
          }
        }
        setLoading(false);
      } catch (err) {
        console.error('Error processing checkout:', err);
        setError('Erro ao processar confirmação. Mas não se preocupe, se o pagamento foi feito, sua assinatura será ativada.');
        setLoading(false);
      }
    };

    processCheckout();
  }, [sessionId, user, refreshSubscription]);

  // Helper to display plan name
  const getDisplayPlanName = () => {
    const paramPlan = searchParams.get('planName');
    if (paramPlan) return paramPlan === 'premium' ? 'Basic' : paramPlan === 'pro' ? 'Pro' : paramPlan;
    return 'Pro'; // Fallback
  };

  if (loading) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'linear-gradient(135deg, #FFF5F7 0%, #FFF8E1 100%)',
        }}
      >
        <Container maxWidth="sm">
          <Paper
            elevation={3}
            sx={{
              p: 4,
              textAlign: 'center',
              backgroundColor: theme.colors.card,
              borderRadius: theme.radii.large,
            }}
          >
            <CircularProgress sx={{ color: theme.colors.primary, mb: 3 }} size={64} />
            <Typography variant="h5" sx={{ fontWeight: 'bold', color: theme.colors.text.primary, mb: 2 }}>
              Finalizando sua assinatura...
            </Typography>
            <Typography sx={{ color: theme.colors.text.secondary }}>
              Estamos configurando sua conta premium
            </Typography>
          </Paper>
        </Container>
      </Box>
    );
  }

  if (error) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'linear-gradient(135deg, #FFF5F7 0%, #FFF8E1 100%)',
        }}
      >
        <Container maxWidth="sm">
          <Paper
            elevation={3}
            sx={{
              p: 4,
              textAlign: 'center',
              backgroundColor: theme.colors.card,
              borderRadius: theme.radii.large,
            }}
          >
            <ErrorIcon sx={{ fontSize: 80, color: '#d32f2f', mb: 3 }} />
            <Typography variant="h5" sx={{ fontWeight: 'bold', color: theme.colors.text.primary, mb: 2 }}>
              Atenção
            </Typography>
            <Alert severity="warning" sx={{ mb: 3 }}>
              {error}
            </Alert>
            <Button
              variant="contained"
              onClick={() => navigate('/settings')}
              sx={{
                backgroundColor: theme.colors.primary,
                color: 'white',
                '&:hover': { backgroundColor: theme.colors.primary, opacity: 0.9 },
              }}
            >
              Verificar Status
            </Button>
          </Paper>
        </Container>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #FFF5F7 0%, #FFF8E1 100%)',
        py: 4,
      }}
    >
      <Container maxWidth="sm">
        <Paper
          elevation={3}
          sx={{
            p: 4,
            textAlign: 'center',
            backgroundColor: theme.colors.card,
            borderRadius: theme.radii.large,
            border: `2px solid ${theme.colors.primary}`,
          }}
        >
          <CheckCircleIcon
            sx={{
              fontSize: 80,
              color: theme.colors.primary,
              mb: 3,
              animation: 'pulse 1.5s ease-in-out',
              '@keyframes pulse': {
                '0%, 100%': { transform: 'scale(1)' },
                '50%': { transform: 'scale(1.1)' },
              },
            }}
          />
          <Typography variant="h4" sx={{ fontWeight: 'bold', color: theme.colors.text.primary, mb: 2 }}>
            Assinatura Confirmada!
          </Typography>
          <Typography variant="h6" sx={{ color: theme.colors.text.secondary, mb: 4 }}>
            Bem-vindo ao Hostly {getDisplayPlanName()}
          </Typography>

          <Alert severity="success" sx={{ mb: 3, textAlign: 'left' }}>
            <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>
              Tudo pronto para começar:
            </Typography>
            <Typography variant="body2">
              • Seu plano <strong>{getDisplayPlanName()}</strong> já está ativo
              <br />
              • Todos os recursos exclusivos foram liberados
              <br />
              • Você pode gerenciar sua assinatura nas configurações
            </Typography>
          </Alert>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Button
              variant="contained"
              fullWidth
              onClick={() => navigate('/')}
              sx={{
                backgroundColor: theme.colors.primary,
                color: 'white',
                py: 1.5,
                fontSize: '1rem',
                fontWeight: 'bold',
                '&:hover': { backgroundColor: theme.colors.primary, opacity: 0.9 },
              }}
            >
              Começar a Usar
            </Button>
            <Button
              variant="outlined"
              fullWidth
              onClick={() => navigate('/settings')}
              sx={{
                borderColor: theme.colors.primary,
                color: theme.colors.primary,
                py: 1.5,
                fontSize: '1rem',
                '&:hover': {
                  borderColor: theme.colors.primary,
                  backgroundColor: `${theme.colors.primary}15`,
                },
              }}
            >
              Ver Configurações
            </Button>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
};
